import React from 'react'
import styled from 'styled-components'
import {Skeleton} from '../BaseComponents'
import type {DonutTone} from './DonutCard'
import {InfoHint} from './InfoHint'
import {DEFAULT_ERROR_LABEL, isBlankNode, StatValue} from './internal/StatValue'

/**
 * StackedBar
 *
 * One whole split into parts: a row of separate pill segments sized by their share (flex), with a
 * legend of swatch, label and figure under it (Orders worked: Host / Co-host / Participant).
 *
 * - A segment above 0 is never narrower than `--space-1`, so a small share stays visible; a
 *   segment at 0 draws nothing (and takes no gap).
 * - The bar is `role="img"`, named by `aria-label` and its figures:
 *   "Orders worked: Host 67, Co-host 6, Participant 11 of 84" (see `stackedBarSummary`).
 * - The bar never hides. A zero total draws the bare track with `emptyLabel` under it; a failure
 *   draws the bare track reading 'Couldn’t load'; loading fills the track with a skeleton.
 * - The legend rows always render, whatever the state. Each figure goes through StatValue, so a
 *   failed figure reads 'Couldn’t load', never '—'. The bar's own `loading` / `error` apply to
 *   every figure; a segment's own `loading` / `error` apply to its figure, and the bar can't be
 *   drawn in proportion without it, so the bar takes that state too.
 *
 * Tones: `navy` → `--viz-navy`, `cyan` → `--viz-cyan`, and `reference` (a neutral share such as
 * Participant) → `--neutral-600`. `neutral` is `--neutral-600` too, not DonutCard's
 * `--neutral-500`: a segment is the whole mark, and `--neutral-500` is under 3:1 (WCAG 1.4.11).
 * DS gap: there is no `--viz-reference` token; swap it in here once it exists.
 *
 * Test ids: `data-testid` is on the bar (with its state line); each segment is
 * `${testId}-segment-${id}`, the legend `${testId}-legend`, and each legend figure
 * `${testId}-${id}` unless the segment names its own.
 *
 * @example
 *   <StackedBar
 *     aria-label="Orders worked"
 *     emptyLabel="No orders this period"
 *     data-testid="staffer-metrics-orders-stacked-bar"
 *     segments={[
 *       {id: 'host', label: 'Host', value: 67, tone: 'navy', 'data-testid': 'staffer-metrics-orders-host'},
 *       {id: 'cohost', label: 'Co-host', value: 6, tone: 'cyan', 'data-testid': 'staffer-metrics-orders-cohost'},
 *       {id: 'participant', label: 'Participant', value: 11, tone: 'reference', 'data-testid': 'staffer-metrics-orders-participant'},
 *     ]}
 *   />
 */

export type StackedBarTone = DonutTone | 'reference'

export interface StackedBarSegment {
  id: string
  label: string
  /** The part's count. Below 0, or not a finite number, counts as 0. */
  value: number
  tone: StackedBarTone
  /** The legend figure in place of `value`, e.g. '1,204'. */
  valueLabel?: React.ReactNode
  /** An InfoHint beside the legend label (`${figureTestId}-info`). */
  hint?: React.ReactNode
  /** The hint's accessible name (default `About ${label}`). */
  hintLabel?: string
  /** This figure is not known yet. */
  loading?: boolean
  /** Truthy = this figure could not be read: 'Couldn’t load'. */
  error?: Error | null | boolean
  /** On the legend figure. Default `${testId}-${id}`. */
  'data-testid'?: string
}

export interface StackedBarProps {
  segments: StackedBarSegment[]
  /**
   * The bar's name only, e.g. "Orders worked", never a pre-built summary. The bar appends its
   * figures or its state itself: "Orders worked: Host 67, Co-host 6, Participant 11 of 84". A
   * summary passed here is read twice (a dev build warns). For an expected string in a test, call
   * `stackedBarSummary`.
   */
  'aria-label': string
  /** The swatch / label / figure rows under the bar (default true). */
  legend?: boolean
  /** The line under a bare track when the total is 0, e.g. "No orders this period". */
  emptyLabel?: string
  /** Every figure is loading. */
  loading?: boolean
  /** Every figure failed (truthy = error). The same type as DashboardCard's `error`. */
  error?: Error | null | boolean
  /** The error text (default "Couldn’t load"). */
  errorLabel?: string
  /** On the bar. */
  'data-testid'?: string
}

export type StackedBarState = 'loading' | 'error' | 'empty' | 'value'

/** A segment's share: a finite count, at least 0. */
export const segmentValue = (segment: Pick<StackedBarSegment, 'value'>): number =>
  Number.isFinite(segment.value) && segment.value > 0 ? segment.value : 0

const COUNT = new Intl.NumberFormat('en-US')

/** The legend figure: `valueLabel`, or the count with thousands separators. */
const figureOf = (segment: StackedBarSegment): React.ReactNode =>
  segment.valueLabel ?? COUNT.format(segmentValue(segment))

/** The figure as words for the summary: a string or number `valueLabel`, else the count. */
const figureText = (segment: StackedBarSegment): string =>
  typeof segment.valueLabel === 'string' || typeof segment.valueLabel === 'number'
    ? String(segment.valueLabel)
    : COUNT.format(segmentValue(segment))

interface BarStateInput {
  segments: StackedBarSegment[]
  loading?: boolean
  error?: Error | null | boolean
}

/**
 * What the bar draws, by the shared precedence loading > error > empty > value. A segment that is
 * loading or failed leaves the proportions unknown, so it puts the whole bar in that state.
 */
export function stackedBarState({segments, loading, error}: BarStateInput): StackedBarState {
  if (loading || segments.some((s) => s.loading)) return 'loading'
  if (error || segments.some((s) => s.error)) return 'error'
  if (segments.every((s) => segmentValue(s) === 0)) return 'empty'
  return 'value'
}

export interface StackedBarSummaryOptions {
  loading?: boolean
  error?: Error | null | boolean
  errorLabel?: string
  emptyLabel?: string
}

/**
 * The bar's accessible name: "Orders worked: Host 67, Co-host 6, Participant 11 of 84".
 * - Loading: "Orders worked: Loading".
 * - The whole bar failed: "Orders worked: Couldn’t load".
 * - A segment failed: its figure reads "Couldn’t load" and the total is left off.
 * - A zero total with an `emptyLabel`: "Orders worked: No orders this period".
 * - No segments and no `emptyLabel`: the name alone.
 */
export function stackedBarSummary(
  name: string,
  segments: StackedBarSegment[],
  {loading, error, errorLabel = DEFAULT_ERROR_LABEL, emptyLabel}: StackedBarSummaryOptions = {},
): string {
  const state = stackedBarState({segments, loading, error})
  if (state === 'loading') return `${name}: Loading`
  if (error) return `${name}: ${errorLabel}`
  if (state === 'empty' && emptyLabel) return `${name}: ${emptyLabel}`
  // No segments and nothing to say: the name alone, not "Orders worked:  of 0".
  if (segments.length === 0) return name

  const parts = segments.map((s) => `${s.label} ${s.error ? errorLabel : figureText(s)}`).join(', ')
  if (state === 'error') return `${name}: ${parts}`
  const total = segments.reduce((sum, s) => sum + segmentValue(s), 0)
  return `${name}: ${parts} of ${COUNT.format(total)}`
}

/**
 * The segment and swatch fills. `reference` / `neutral` are `--neutral-600`: theme-swapped, and at
 * least 3:1 against the card in both themes.
 */
const TONE_VARS: Record<StackedBarTone, string> = {
  cyan: 'var(--viz-cyan)',
  'cyan-soft': 'var(--viz-cyan-soft)',
  blue: 'var(--viz-blue)',
  'blue-soft': 'var(--viz-blue-soft)',
  navy: 'var(--viz-navy)',
  'navy-soft': 'var(--viz-navy-soft)',
  warning: 'var(--warning-500)',
  error: 'var(--error-500)',
  neutral: 'var(--neutral-600)',
  reference: 'var(--neutral-600)',
}

/** Bar, then legend, with the mock's 12px between (`.ep-stack` margin-bottom). */
const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  font-family: var(--font-sans);
`

/** The bar and, under a bare track, its state line. */
const Figure = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
`

/** Separate pill segments with a gap between them, not one clipped bar (mock `.ep-stack`). */
const Track = styled.div<{$bare: boolean}>`
  display: flex;
  gap: var(--space-1);
  height: var(--space-2);
  border-radius: var(--radius-pill);
  background-color: ${({$bare}) => ($bare ? 'var(--viz-track)' : 'transparent')};
`

/** Grows by its share (inline `flex-grow`), never below `--space-1`. */
const Segment = styled.span<{$tone: StackedBarTone}>`
  display: block;
  flex-shrink: 1;
  flex-basis: 0;
  min-width: var(--space-1);
  height: 100%;
  border-radius: var(--radius-pill);
  background-color: ${({$tone}) => TONE_VARS[$tone]};
`

const TrackSkeleton = styled(Skeleton)`
  border-radius: var(--radius-pill);
`

const StateLine = styled.div`
  font-size: var(--fs-caption);
  line-height: var(--lh-normal);
  color: var(--text-secondary);
`

const Legend = styled.dl`
  margin: 0;
  font-size: var(--fs-caption);
  line-height: var(--lh-normal);
  color: var(--text-primary);
`

/** Swatch, label, figure pushed right (mock `.ep-share`). */
const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) 0;
`

const Term = styled.dt`
  display: flex;
  align-items: center;
  gap: var(--space-1);
  min-width: 0;
  margin: 0;
`

/** A small square (mock `.ep-sw`: 9px, 2px corners). */
const Swatch = styled.span<{$tone: StackedBarTone}>`
  flex: 0 0 auto;
  width: var(--space-2);
  height: var(--space-2);
  margin-right: var(--space-1);
  border-radius: calc(var(--radius-sm) / 4);
  background-color: ${({$tone}) => TONE_VARS[$tone]};
`

const Value = styled.dd`
  margin: 0 0 0 auto;
  text-align: right;
  font-weight: var(--fw-semibold);
  font-variant-numeric: tabular-nums;
  color: var(--primary-800);
`

export function StackedBar({
  segments,
  'aria-label': name,
  legend = true,
  emptyLabel,
  loading = false,
  error,
  errorLabel = DEFAULT_ERROR_LABEL,
  'data-testid': testId,
}: StackedBarProps) {
  const state = stackedBarState({segments, loading, error})
  const summary = stackedBarSummary(name, segments, {loading, error, errorLabel, emptyLabel})
  const testIdFor = (suffix: string) => (testId ? `${testId}-${suffix}` : undefined)

  // A name with ': ' in it is almost always a summary built by the caller, which the bar would
  // then read twice ("Orders worked: Host 67, … of 84: Host 67, …").
  React.useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && name.includes(': ')) {
      // eslint-disable-next-line no-console
      console.warn(
        `<StackedBar>: aria-label "${name}" looks like a summary. Pass the name only (e.g. "Orders worked"); the bar adds its figures.`,
      )
    }
  }, [name])

  const stateLine =
    state === 'error' ? (
      <StatValue error errorLabel={errorLabel} />
    ) : state === 'empty' && emptyLabel ? (
      emptyLabel
    ) : null

  return (
    <Root aria-busy={state === 'loading' || undefined}>
      {/* The name carries the figures and the state, so what the bar shows is presentational. */}
      <Figure role="img" aria-label={summary} data-testid={testId} data-state={state}>
        <Track $bare={state !== 'value'}>
          {state === 'loading' ? (
            <TrackSkeleton aria-hidden="true" width="100%" height="100%" />
          ) : state === 'value' ? (
            segments
              .filter((segment) => segmentValue(segment) > 0)
              .map((segment) => (
                <Segment
                  key={segment.id}
                  $tone={segment.tone}
                  data-tone={segment.tone}
                  data-testid={testIdFor(`segment-${segment.id}`)}
                  style={{flexGrow: segmentValue(segment)}}
                />
              ))
          ) : null}
        </Track>
        {stateLine === null ? null : (
          <StateLine data-testid={testIdFor('state')}>{stateLine}</StateLine>
        )}
      </Figure>

      {legend ? (
        <Legend data-testid={testIdFor('legend')}>
          {segments.map((segment) => {
            const figureTestId = segment['data-testid'] ?? testIdFor(segment.id)
            const hintLabel = segment.hintLabel ?? `About ${segment.label}`
            return (
              <LegendRow key={segment.id}>
                <Term>
                  <Swatch $tone={segment.tone} data-tone={segment.tone} aria-hidden="true" />
                  {segment.label}
                  {isBlankNode(segment.hint) ? null : (
                    <InfoHint
                      label={hintLabel}
                      content={segment.hint}
                      data-testid={figureTestId ? `${figureTestId}-info` : undefined}
                    />
                  )}
                </Term>
                <Value data-testid={figureTestId}>
                  <StatValue
                    value={figureOf(segment)}
                    loading={loading || Boolean(segment.loading)}
                    error={Boolean(error) || Boolean(segment.error)}
                    errorLabel={errorLabel}
                  />
                </Value>
              </LegendRow>
            )
          })}
        </Legend>
      ) : null}
    </Root>
  )
}
